import { CreditCardCriteria, Filter, RealTimeResource } from '@nemuru-eng/js-api-client';
import { generatePath, useNavigate } from 'react-router-dom';
import { SWRConfig } from 'swr';

import { ALLOWED_CREDIT_CARD_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/CreditCard/List/constants/creditCardStatusConfig';
import { CreditCardDataGridFields } from '@/components/modules/CreditCard/List/CreditCardDataGridFields/CreditCardDataGridFields';
import { useFetchCreditCardList } from '@/components/modules/CreditCard/List/hooks/useFetchCreditCardList';
import { DetailedLoanStatusTag } from '@/components/shared/components/DetailedLoanStatusTag';
import { CustomGridColDef } from '@/components/shared/components/FetchingDataGrid/FetchingDataGrid';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ApplicationType } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCollectionScope } from '@/hooks/useCollectionScope';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

export const CreditCardList = ({
  listProps,
}: {
  listProps?: {
    additionalFilters?: Filter[];
  };
}) => {
  const { fetchCreditCardList, fetchXLSXExport } = useFetchCreditCardList({
    additionalFilters: listProps?.additionalFilters,
  });
  const navigate = useNavigate();
  const scope = useCollectionScope();

  const {
    state: { lastViewedLoanId },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const tableColumns = CreditCardDataGridFields();

  const getDynamicPagePathByOnboardingFulfilledBy = (isLoan: boolean) => {
    if (isLoan) {
      return DYNAMIC_PAGE_PATHS.CONSUMER_LOAN;
    } else {
      return DYNAMIC_PAGE_PATHS.CREDIT_CARD;
    }
  };

  return (
    <SWRConfig value={{ revalidateIfStale: false }}>
      <MultiFilteredList
        fetchList={fetchCreditCardList}
        getXLSXExport={fetchXLSXExport}
        dataGridFields={tableColumns as CustomGridColDef[]}
        liveUpdates={{ resource: RealTimeResource.CREDIT_CARD_LIST, scope }}
        onRowClick={(selectedRow) => {
          const row = selectedRow.row as CreditCardCriteria;
          updateOperationsContext({ lastViewedLoanId: selectedRow.row.id });
          navigate(
            generatePath(
              getDynamicPagePathByOnboardingFulfilledBy(row.isLoan),
              row.isLoan ? { loanId: row.id } : { creditCardId: row.id },
            ),
          );
        }}
        getRowClassName={(selectedRow) => {
          if (selectedRow.row.id === lastViewedLoanId) return 'last-viewed-application';
          else return '';
        }}
        multiselectProps={{
          field: 'status_value',
          label: <Text tId={'LOAN_STATUS_EXTERNAL'} />,
          defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
          items: ALLOWED_CREDIT_CARD_STATUSES_FOR_MULTI_SELECT_FILTER,
          checkBoxRender: (value) => {
            return <DetailedLoanStatusTag status={value} applicationType={ApplicationType.CARD} />;
          },
          filtersValueHandler: (valueArray: string[]) => {
            return valueArray;
          },
          defaultItems: ALLOWED_CREDIT_CARD_STATUSES_FOR_MULTI_SELECT_FILTER,
        }}
        dateFilterProps={{
          dateField: 'updated_at',
        }}
        defaultOrderBy={'updated_at'}
        showExport
        disableSelectionOnClick
      />
    </SWRConfig>
  );
};

export const CreditCardListModule = withPermissionsModule(CreditCardList);
